import React from 'react'

import Input from '../ui/input'
import Checkbox from '../ui/input/checkbox'
import Button, { ButtonVariant } from '../ui/button'
import InvoiceOverlayModal from '../modals'
import { TBusiness } from '../../types'

export type GuestTaxDraft = {
  'businessDetails.taxRegistrationNumber': string
  'businessDetails.enableTax': boolean
  'businessDetails.tax.name': string
  'businessDetails.tax.percentage': string | number
}

export type GuestTaxModalProps = {
  open: boolean
  onClose: () => void
  onSaved: (draft: GuestTaxDraft) => void
  initialDraft: GuestTaxDraft
}

export const buildGuestTaxDraft = (businessDetails: TBusiness): GuestTaxDraft => ({
  'businessDetails.taxRegistrationNumber': businessDetails.taxRegistrationNumber || '',
  'businessDetails.enableTax': !!businessDetails.enableTax,
  'businessDetails.tax.name': businessDetails.tax?.name || '',
  'businessDetails.tax.percentage': businessDetails.tax?.percentage ?? '',
})

const GuestTaxModal: React.FC<GuestTaxModalProps> = ({
  open,
  onClose,
  onSaved,
  initialDraft,
}) => {
  const [taxDraft, setTaxDraft] = React.useState<GuestTaxDraft>(initialDraft)
  const prevOpen = React.useRef(false)

  React.useEffect(() => {
    if (open && !prevOpen.current) {
      setTaxDraft(initialDraft)
    }
    prevOpen.current = open
  }, [open, initialDraft])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = event.target
    const checked = event.target.checked
    setTaxDraft((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSave = () => {
    onSaved(taxDraft)
    onClose()
  }

  return (
    <InvoiceOverlayModal open={open} onClose={onClose} title='Tax settings'>
      <div className='mb-3'>
        <Input
          label='Tax Registration Number'
          name='businessDetails.taxRegistrationNumber'
          id='businessDetails.taxRegistrationNumber'
          value={taxDraft['businessDetails.taxRegistrationNumber']}
          onChange={handleChange}
          autoFocus
        />
      </div>
      <div className='mb-3'>
        <Checkbox
          label='Enable tax on invoices'
          name='businessDetails.enableTax'
          id='businessDetails.enableTax'
          checked={taxDraft['businessDetails.enableTax']}
          onChange={handleChange}
        />
      </div>
      {taxDraft['businessDetails.enableTax'] && (
        <>
          <div className='mb-3'>
            <Input
              label='Tax Name'
              name='businessDetails.tax.name'
              id='businessDetails.tax.name'
              value={taxDraft['businessDetails.tax.name']}
              onChange={handleChange}
              required
            />
          </div>
          <div className='mb-3'>
            <Input
              label='Tax Percentage'
              name='businessDetails.tax.percentage'
              id='businessDetails.tax.percentage'
              type='number'
              value={taxDraft['businessDetails.tax.percentage']}
              onChange={handleChange}
              required
            />
          </div>
        </>
      )}
      <div className='d-flex justify-content-end gap-2 pt-2'>
        <Button type='button' variant={ButtonVariant.LIGHT} onClick={onClose}>
          Cancel
        </Button>
        <Button type='button' variant={ButtonVariant.PRIMARY} onClick={handleSave}>
          Apply
        </Button>
      </div>
    </InvoiceOverlayModal>
  )
}

export default GuestTaxModal
